// crt.js — Hercules-style monochrome CRT renderer.
//
// GEOMETRY IS LIVE, not fixed. The guest CRTC (see libvm86/devices/hercules.c)
// is a real MC6845-style register set: it can and does reprogram width/height/
// stride at runtime (boot POST alone walks it through several text-mode
// geometries before settling on 640x400 graphics — e.g. 405x348, 360x348,
// 360x400 -> 640x400). This module must never assume any one of those sizes;
// it resizes its canvas backing store to whatever vm86_display_info() reports
// every frame (see render() below). The FIXED 4:3 "tube" framing / non-square
// pixel presentation is a CSS concern (web/ui/display.css) layered on top of
// this raw per-mode bitmap — crt.js only ever decodes pixels 1:1 into an
// image the size of the live mode.
//
// Reads guest VRAM straight out of the wasm heap and decodes it with the
// 4-bank interleave recipe documented in web/wasm_bridge.c (vm86_mem_ptr):
//
//   base       = memBase + 0x88000                 // HEAPU8 index of VRAM
//   window     = 32768                             // wrap all offsets mod this
//   startByte  = (start_address * 2) % window      // CRTC start is word-addressed
//   pageOffset = page_select ? 0x8000 : 0
//   effStart   = (startByte + pageOffset) % window
//   rowBase(y) = (effStart + ((y&3)<<13) + ((y>>2)*stride)) % window   // graphics
//   byte       = HEAPU8[base + rowBase(y) + (x>>3)]
//   pixelOn    = (byte >> (7-(x&7))) & 1            // bit7 = leftmost pixel
//
// Text mode drops the ((y&3)<<13) bank term (linear). We only present the CRT
// as the main screen when it reports video+graphics AND has non-blank VRAM;
// app.js owns that decision and simply skips render() otherwise.

import { ScaledPresenter } from './blit.js';

// ---- Phosphor palettes: 1980s monochrome monitor simulations -------------
// Each entry is the physical phosphor of a period mono CRT: a near-black glass
// (bg) that a single phosphor colour (fg) glows against. `glow` is that colour
// as an "r,g,b" triple so the CSS .crt-glow bloom/scanlines and the tube-face
// letterbox can be tinted to match (display.css consumes --crt-glow-tint /
// --crt-face). `blue` is the machine's original dark-navy default.
//
//   name  | phosphor | fg family     | bg (tube glass)
//   ------+----------+---------------+----------------
//   blue  | (default)| pale blue-white #d8e8f0 | deep blue-black #101a2e
//   green | P1       | #33ff33 green | near-black green #041206
//   amber | P3       | #ffb000 amber | near-black brown #170f02
//   white | P4       | paper-white   | near-black grey  #0d0d0f
export const PHOSPHORS = {
  blue:  { label: '蓝白', bg: [0x10, 0x1a, 0x2e], fg: [0xd8, 0xe8, 0xf0], glow: '150,190,230' },
  green: { label: 'P1 绿', bg: [0x04, 0x12, 0x06], fg: [0x33, 0xff, 0x33], glow: '70,255,90' },
  amber: { label: 'P3 琥珀', bg: [0x17, 0x0f, 0x02], fg: [0xff, 0xb0, 0x00], glow: '255,176,40' },
  white: { label: 'P4 白', bg: [0x0d, 0x0d, 0x0f], fg: [0xf0, 0xf0, 0xe6], glow: '235,235,225' },
  // 像素 is not a tube — it's a modern flat panel: pure white-on-black, integer
  // scaled 1:1 pixels, and NO simulated artifacts (pixel:true drives crisp mode
  // and display.css suppresses glow/vignette/rounding under .is-crisp).
  pixel: { label: '像素', bg: [0x00, 0x00, 0x00], fg: [0xff, 0xff, 0xff], glow: '255,255,255', pixel: true },
};
export const DEFAULT_PHOSPHOR = 'blue';

const rgb = (a) => `${a[0]},${a[1]},${a[2]}`;

export class CrtRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    // Native mode raster lives on the presenter's offscreen canvas; the
    // visible canvas is backed at CSS-box x devicePixelRatio, so the 4:3
    // tube stretch (or any other fractional zoom) is smoothed/antialiased
    // at device resolution while integer scales stay pixel-perfect.
    this.presenter = new ScaledPresenter(canvas);
    this.image = null;
    this.lit = 0; // lit-pixel count of the last frame (used for blank test)

    // DOM handles for CSS-var / layout coordination.
    this.inset = canvas.parentElement;               // .crt-inset (the tube glass)
    this.region = canvas.closest('#screen-region');  // scope for the CSS vars

    // Display options (app.js persists them in localStorage and pushes them in).
    this.crisp = false;                              // pixel-perfect vs 4:3 stretch
    this._phosphor = DEFAULT_PHOSPHOR;
    this.bg = PHOSPHORS[DEFAULT_PHOSPHOR].bg;
    this.fg = PHOSPHORS[DEFAULT_PHOSPHOR].fg;
    this._layoutKey = '';                            // dedupe crisp relayout work

    // Re-run the crisp integer-scale layout when the tube box changes size.
    if (typeof ResizeObserver !== 'undefined' && this.inset) {
      this._ro = new ResizeObserver(() => { this._layoutKey = ''; this._layout(); });
      this._ro.observe(this.inset);
    }
    this.setPhosphor(DEFAULT_PHOSPHOR);
  }

  // Select a phosphor palette. Repaints on the next render(); also pushes the
  // matching glow tint + tube-face colour to CSS so the bloom/scanline overlay
  // and the (crisp-mode) letterbox track the phosphor even while blank.
  setPhosphor(name) {
    const p = PHOSPHORS[name] || PHOSPHORS[DEFAULT_PHOSPHOR];
    this._phosphor = PHOSPHORS[name] ? name : DEFAULT_PHOSPHOR;
    this.bg = p.bg;
    this.fg = p.fg;
    if (this.region) {
      this.region.style.setProperty('--crt-glow-tint', p.glow);
      this.region.style.setProperty('--crt-face', `rgb(${rgb(p.bg)})`);
    }
    // The display TYPE implies the presentation: tubes stretch 4:3 with the
    // period artifacts; the 像素 flat panel renders clean integer-scaled pixels.
    this.setCrisp(!!p.pixel);
  }

  get phosphor() { return this._phosphor; }

  // Toggle the flat-panel ("crisp") presentation: square 1:1 logical pixels,
  // integer-scaled to the largest fit inside a frame that HUGS the raster's own
  // aspect (display.css .is-crisp), versus the default period tube — a 4:3
  // glass the raster is stretched non-square to fill.
  setCrisp(on) {
    this.crisp = !!on;
    if (this.region) this.region.classList.toggle('is-crisp', this.crisp);
    this._layoutKey = '';
    this._layout();
  }

  // In crisp mode, size #crt to native_dimensions × N (largest integer N that
  // fits the tube box) and let CSS centre it (letterbox). In 4:3 mode, clear the
  // inline size so style.css's width:100%/height:100% stretch takes over.
  // The raster's NATIVE geometry lives in this.image (the visible canvas is
  // backed at device-pixel size by ScaledPresenter, so canvas.width/height no
  // longer mean "mode geometry").
  _layout() {
    const cv = this.canvas;
    const w = this.image ? this.image.width : 0;
    const h = this.image ? this.image.height : 0;
    // Publish the live raster's aspect ratio so the flat-panel frame (.is-crisp
    // in display.css) can HUG it instead of holding a 4:3 box. Independent of
    // the crisp fit below, so it tracks every mode-geometry change too.
    if (this.region && w && h) {
      this.region.style.setProperty('--raster-ar', (w / h).toFixed(4));
    }
    if (!this.crisp) {
      if (cv.style.width || cv.style.height) { cv.style.width = ''; cv.style.height = ''; }
      this._layoutKey = 'stretch';
      return;
    }
    const box = this.inset ? this.inset.getBoundingClientRect() : null;
    if (!box || !box.width || !box.height || !w || !h) return;
    const key = `crisp:${w}x${h}@${Math.round(box.width)}x${Math.round(box.height)}`;
    if (key === this._layoutKey) return;
    this._layoutKey = key;
    const n = Math.max(1, Math.floor(Math.min(box.width / w, box.height / h)));
    cv.style.width = `${w * n}px`;
    cv.style.height = `${h * n}px`;
  }

  // info: {mode,width,height,vram,stride,start,page,window}
  // returns lit-pixel count (0 == blank).
  render(machine, info) {
    const { width, height, vram, stride, start, page, window: win } = info;
    const graphics = (info.mode & 1) !== 0;
    if (!width || !height) { this.lit = 0; return 0; }

    if (!this.image || this.image.width !== width || this.image.height !== height) {
      this.image = new ImageData(width, height);
      this._layoutKey = '';               // mode geometry changed: relayout crisp
    }

    const heap = machine.heap();          // re-read: reassigned on memory growth
    const base = machine.memBase() + vram;
    const px = this.image.data;
    const bpr = stride || (width >> 3);
    const BG = this.bg, FG = this.fg;

    // opaque background first
    for (let i = 0; i < px.length; i += 4) {
      px[i] = BG[0]; px[i + 1] = BG[1]; px[i + 2] = BG[2]; px[i + 3] = 255;
    }

    const startByte = ((start * 2) >>> 0) % win;
    const effStart = (startByte + (page ? 0x8000 : 0)) % win;
    let lit = 0;
    for (let y = 0; y < height; y++) {
      const bank = graphics ? ((y & 3) << 13) : 0;
      const rowBase = (effStart + bank + ((y >> 2) * bpr)) % win;
      let outRow = y * width * 4;
      for (let bx = 0; bx < bpr; bx++) {
        const v = heap[base + ((rowBase + bx) % win)];
        if (!v) continue;
        const x0 = bx << 3;
        for (let bit = 0; bit < 8; bit++) {
          if (!((v >> (7 - bit)) & 1)) continue;
          const x = x0 + bit;
          if (x >= width) break;
          lit++;
          const o = outRow + x * 4;
          px[o] = FG[0]; px[o + 1] = FG[1]; px[o + 2] = FG[2];
        }
      }
    }

    if (this.crisp) this._layout();
    this.presenter.present(this.image);
    this.lit = lit;
    return lit;
  }

  clear() {
    this.presenter.fill(`rgb(${rgb(this.bg)})`);
    this.lit = 0;
  }

  // POWER OFF: the tube is de-energised — no phosphor, no glow, just dead dark
  // glass (NOT the "no signal" plate). app.js hides the plate/glow via CSS and
  // calls this to blank the raster to pure black.
  powerOff() {
    this.presenter.fill('#000');
    this.lit = 0;
  }
}
